import React from 'react';
import ActionButton, { ActionButtonProps } from 'components/ActionButton';
import { Mute, Volume } from 'icons';
import * as S from './styles';

export type MediaActionButtonState = 'muted' | 'unmuted';

export type MediaActionButtonProps<
  As extends React.ElementType = 'button',
  Container extends React.ElementType = 'div',
> = ActionButtonProps<As> & {
  containerAs?: Container;
  containerProps?: React.ComponentPropsWithoutRef<Container>;
  /**
   * The state that the buttons should display.  When state is "muted", the button
   * will render a muted icon and when the state is "unmuted", the button will render
   * the volume icon.
   */
  state: MediaActionButtonState;
};

const STATE_ICON: Record<
  MediaActionButtonState,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  muted: Mute,
  unmuted: Volume,
};

function ForwardedMediaActionButton<
  T extends Element = HTMLDivElement,
  As extends React.ElementType = 'button',
  Container extends React.ElementType = 'div',
>(
  {
    as,
    containerAs,
    containerProps,
    state,
    variant = 'frosted',
    ...props
  }: MediaActionButtonProps<As, Container>,
  ref: React.ForwardedRef<T>,
): React.ReactElement | null {
  const Icon = STATE_ICON[state];
  const Component: React.ElementType = as ?? 'button';
  const ContainerComponent: React.ElementType = containerAs ?? 'div';

  return (
    <S.Root as={ContainerComponent} {...containerProps} ref={ref}>
      <ActionButton
        aria-label={state === 'muted' ? 'unmute' : 'mute'}
        as={Component}
        variant={variant}
        {...props}
      >
        <Icon />
      </ActionButton>
    </S.Root>
  );
}

/**
 * An action button used as a media element overlay.
 *
 * The button supports mute and unmute, with the idea being that the button
 * remains centered over a video player.
 *
 * This component provides an `aria-label` for the button that can be overriden.
 * If the `state` prop is "muted", then the button renders an `aria-label` of "unmute",
 * since that is what is assumed will happen when the button is clicked.  Similarly,
 * if the `state` is "unmuted", then an `aria-label` of "mute" is used.
 */
export const MediaActionButton = React.forwardRef(
  ForwardedMediaActionButton,
) as <
  T extends Element = HTMLDivElement,
  As extends React.ElementType = 'button',
  Container extends React.ElementType = 'div',
>(
  props: MediaActionButtonProps<As, Container> & { ref?: React.Ref<T> },
) => React.ReactElement | null;

export default MediaActionButton;
